import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Switch,
  Tooltip,
  TablePagination,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchWorkOrdersStart,
  fetchDropdownsStart,
  createWorkOrderStart,
  setSearchTerm,
  setStatusFilter,
  setIsReworkFilter,
  setPage,
  setPageSize,
  toggleLiveUpdates,
  clearError,
} from '../../store/reducers/workOrderSlice';
import { CreateWorkOrderRequest, WorkOrder } from '../../services/workOrderApi';

/**
 * WorkOrderList component that displays a list of work orders with filtering, 
 * search, and creation capabilities
 */
const WorkOrderList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const {
    workOrders,
    loading,
    error,
    currentPage,
    pageSize,
    totalCount,
    searchTerm,
    statusFilter,
    isReworkFilter,
    workOrderStatuses,
    routeCards,
    loadingDropdowns,
    liveUpdates,
    lastUpdateTimestamp,
  } = useSelector((state: RootState) => state.workOrder);

  // Ensure we have arrays to iterate over
  const statusOptions = Array.isArray(workOrderStatuses) ? workOrderStatuses : [];
  const routeCardOptions = Array.isArray(routeCards) ? routeCards : [];
  const safeWorkOrders = Array.isArray(workOrders) ? workOrders : [];

  // Debug logging
  console.log('WorkOrderList - Redux state:', {
    workOrders,
    workOrdersType: typeof workOrders,
    workOrdersIsArray: Array.isArray(workOrders),
    workOrdersLength: workOrders?.length,
    safeWorkOrders,
    safeWorkOrdersLength: safeWorkOrders.length,
    loading,
    error,
    totalCount
  });

  const { purchaseOrders } = useSelector((state: RootState) => state.purchaseOrder);
  const purchaseOrderOptions = Array.isArray(purchaseOrders) ? purchaseOrders : [];

  // Local state
  const [showFilters, setShowFilters] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState<CreateWorkOrderRequest>({
    POId: '',
    routeCardData: { RCTId: '' },
    woSerialData: [],
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchWorkOrdersStart());
    dispatch(fetchDropdownsStart());
  }, [dispatch]);

  // Auto-refresh is handled by the saga, no need for component-level polling
  // useEffect(() => {
  //   if (liveUpdates) {
  //     const interval = setInterval(() => {
  //       dispatch(fetchWorkOrdersStart());
  //     }, 30000); // Refresh every 30 seconds
  //
  //     return () => clearInterval(interval);
  //   }
  // }, [liveUpdates, dispatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setStatusFilter(event.target.value as number | null));
  };

  const handleReworkFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setIsReworkFilter(event.target.checked));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1)); // Material-UI is 0-based, our API is 1-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRefresh = () => {
    dispatch(fetchWorkOrdersStart());
  };

  const handleToggleLiveUpdates = () => {
    dispatch(toggleLiveUpdates());
  };

  const handleViewWorkOrder = (workOrder: WorkOrder) => {
    navigate(`/work-orders/edit/${workOrder.id}`);
  };

  const handleEditWorkOrder = (workOrder: WorkOrder) => {
    navigate(`/work-orders/edit/${workOrder.id}`);
  };

  const handleCreateWorkOrder = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setNewWorkOrder({
      POId: '',
      routeCardData: { RCTId: '' },
      woSerialData: [],
    });
  };

  const handleSaveWorkOrder = () => {
    if (newWorkOrder.POId && newWorkOrder.routeCardData.RCTId) {
      dispatch(createWorkOrderStart(newWorkOrder));
      handleCloseCreateModal();
    }
  };

  const handlePurchaseOrderChange = (poId: string) => {
    setNewWorkOrder(prev => ({ ...prev, POId: poId }));
    // TODO: Trigger route card selection based on PO
  };

  const handleRouteCardChange = (rctId: string) => {
    setNewWorkOrder(prev => ({
      ...prev,
      routeCardData: { RCTId: rctId }
    }));
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const getStatusColor = (statusId: number): 'default' | 'primary' | 'secondary' => {
    switch (statusId) {
      case 1: return 'primary'; // Open
      case 2: return 'secondary'; // In Progress
      case 3: return 'default'; // Completed
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading Work Orders...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Work Orders
          </Typography>
          {lastUpdateTimestamp && (
            <Typography variant="caption" color="textSecondary">
              Last updated: {formatDate(lastUpdateTimestamp)}
            </Typography>
          )}
        </Box>
        <Box display="flex" style={{ gap: '8px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={liveUpdates}
                onChange={handleToggleLiveUpdates}
                color="primary"
              />
            }
            label="Live Updates"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateWorkOrder}
          >
            Create Work Order
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
        <Box display="flex" alignItems="center" style={{ gap: '1rem' }}>
          <TextField
            label="Search Work Orders"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
            style={{ minWidth: '200px' }}
          />
          
          <FormControl variant="outlined" size="small" style={{ minWidth: '150px' }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter || ''}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={isReworkFilter || false}
                onChange={handleReworkFilterChange}
                color="primary"
              />
            }
            label="Rework Only"
          />

          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />

      {/* Work Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Work Order Number</TableCell>
              <TableCell>Purchase Order</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Route Card</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={30} />
                  <Typography variant="body2" style={{ marginLeft: '1rem' }}>
                    Loading work orders...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : safeWorkOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No work orders found. {searchTerm && `Try adjusting your search term.`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeWorkOrders.map((workOrder) => (
                <TableRow key={workOrder.id} hover>
                  <TableCell>{workOrder.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {workOrder.wo_num}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {workOrder.purchase_order?.id || 'N/A'}
                    </Typography>
                    {workOrder.client && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {workOrder.client.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{workOrder.qty}</TableCell>
                  <TableCell>
                    <Tooltip title="View Route Card Details">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {/* TODO: Show route card details */}}
                      >
                        {workOrder.route_card?.name || 'N/A'}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={workOrder.status?.name || 'Unknown'}
                      color={getStatusColor(workOrder.wo_status_id)}
                      size="small"
                    />
                    {workOrder.is_rework && (
                      <Chip
                        label="Rework"
                        color="secondary"
                        size="small"
                        style={{ marginLeft: '0.5rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewWorkOrder(workOrder)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Work Order">
                      <IconButton
                        size="small"
                        onClick={() => handleEditWorkOrder(workOrder)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1} // Material-UI is 0-based, our API is 1-based
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Work orders per page:"
        />
      </TableContainer>

      {/* Create Work Order Modal */}
      <Dialog 
        open={createModalOpen} 
        onClose={handleCloseCreateModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ paddingTop: '1rem' }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Purchase Order</InputLabel>
                <Select
                  value={newWorkOrder.POId}
                  onChange={(e) => handlePurchaseOrderChange(e.target.value as string)}
                >
                  {purchaseOrderOptions.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.Number} - {po.Client?.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Route Card</InputLabel>
                <Select
                  value={newWorkOrder.routeCardData.RCTId}
                  onChange={(e) => handleRouteCardChange(e.target.value as string)}
                  disabled={!newWorkOrder.POId}
                >
                  {routeCardOptions.map((rc) => (
                    <MenuItem key={rc.id} value={rc.id}>
                      {rc.Name} - {rc.Description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* TODO: Add serial selection component */}
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Serial selection will be available after selecting a Purchase Order and Route Card.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal}>Cancel</Button>
          <Button
            onClick={handleSaveWorkOrder}
            color="primary"
            variant="contained"
            disabled={!newWorkOrder.POId || !newWorkOrder.routeCardData.RCTId}
          >
            Create Work Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrderList; 